import { z } from 'zod';

/**
 * Drone operator-portal vocabulary + the credential-expiry derivation — Drone D1 #318.
 *
 * The operator-facing portal (onboarding · compliance · fleet/availability) reads + writes
 * the operator's OWN agency, the #317 drone_assets, and the #318 operator_credentials. This
 * module carries the controlled vocab the API validates against and the PURE expiry-flag
 * derivation, so both the route handlers and the web surface share one source of truth
 * (lockstep, pinned by drone-operator-portal.test.ts).
 *
 * Same anti-clobber discipline as the capability taxonomy (./drone-taxonomy): the canonical
 * list is THIS const-tuple. Unlike the FK-lookup vocab, the availability / credential-status
 * values are NOT a DB FK — they default a free-text column (mirroring drone_assets.status),
 * validated at the API boundary; the formal asset state vocab/machine is #319.
 */

/**
 * Operational availability of a fleet asset (#318) — distinct from drone_assets.status
 * (the active / retired LIFECYCLE). Drives the operator's availability surface; #320
 * matching will read it to filter taskable supply.
 *   - available    — ready for tasking.
 *   - unavailable  — temporarily not taskable (crew / weather / stood-down).
 *   - maintenance  — out for maintenance.
 */
export const DRONE_AVAILABILITY_STATUSES = ['available', 'unavailable', 'maintenance'] as const;

export const DroneAvailabilityStatusSchema = z.enum(DRONE_AVAILABILITY_STATUSES);
export type DroneAvailabilityStatus = z.infer<typeof DroneAvailabilityStatusSchema>;

/**
 * Operator-credential lifecycle status (#318) — the operator-set state of a compliance
 * record (NOT the derived expiry flag). A credential is soft-retired by setting `revoked`
 * / `superseded`, never hard-deleted.
 *   - active      — the current, in-force credential.
 *   - revoked     — withdrawn / no longer valid.
 *   - superseded  — replaced by a newer credential.
 */
export const OPERATOR_CREDENTIAL_STATUSES = ['active', 'revoked', 'superseded'] as const;

export const OperatorCredentialStatusSchema = z.enum(OPERATOR_CREDENTIAL_STATUSES);
export type OperatorCredentialStatus = z.infer<typeof OperatorCredentialStatusSchema>;

/**
 * The DERIVED compliance-expiry flag (#318) — computed at read-time from `expires_at`,
 * never stored, never scheduled (no cron). Record-keeping only: the platform does NOT
 * externally verify a credential; it only surfaces how close to expiry the operator's
 * own recorded date is.
 *   - none      — no expiry date recorded.
 *   - valid     — expiry is beyond the warning window.
 *   - expiring  — expiry is within the warning window (default 30 days).
 *   - expired   — the expiry date has passed.
 */
export const CREDENTIAL_EXPIRY_FLAGS = ['none', 'valid', 'expiring', 'expired'] as const;
export type CredentialExpiryFlag = (typeof CREDENTIAL_EXPIRY_FLAGS)[number];

/** Default warning window: a credential expiring within this many days flags `expiring`. */
export const CREDENTIAL_EXPIRY_WINDOW_DAYS = 30;

/**
 * Derive the compliance-expiry flag from a recorded expiry date. PURE — the caller injects
 * `nowMs` (Date.now() in a handler, a fixed value in a test) so the function is
 * deterministic and unit-testable. A null/absent/unparseable date is `none` (no expiry
 * tracked — never a false `expired`).
 *
 * @param expiresAt  the recorded expiry (ISO string, Date, or null/undefined)
 * @param nowMs      the current time in epoch ms (injected — Date.now() at the call site)
 * @param windowDays the warning window in days (default CREDENTIAL_EXPIRY_WINDOW_DAYS)
 */
export function deriveCredentialExpiryFlag(
  expiresAt: string | Date | null | undefined,
  nowMs: number,
  windowDays: number = CREDENTIAL_EXPIRY_WINDOW_DAYS,
): CredentialExpiryFlag {
  if (expiresAt === null || expiresAt === undefined) return 'none';
  const expiryMs = expiresAt instanceof Date ? expiresAt.getTime() : Date.parse(expiresAt);
  if (Number.isNaN(expiryMs)) return 'none';
  if (expiryMs <= nowMs) return 'expired';
  const windowMs = windowDays * 24 * 60 * 60 * 1000;
  if (expiryMs <= nowMs + windowMs) return 'expiring';
  return 'valid';
}

/** Coerce an arbitrary value into a `DroneAvailabilityStatus`, `null` on unexpected input. */
export function parseDroneAvailabilityStatus(value: unknown): DroneAvailabilityStatus | null {
  const parsed = DroneAvailabilityStatusSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

/** Coerce an arbitrary value into an `OperatorCredentialStatus`, `null` on unexpected input. */
export function parseOperatorCredentialStatus(value: unknown): OperatorCredentialStatus | null {
  const parsed = OperatorCredentialStatusSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}
